import { LegalLayout } from "@/components/LegalLayout";

const sections = [
  {
    h: "1. Who we are",
    p: [
      "Swishr Central (\u201cSwishr Central\u201d, \u201cwe\u201d, \u201cus\u201d, \u201cour\u201d) is a software-as-a-service platform for personal assistants, executive assistants and family-office teams. This policy explains how we collect and use your personal data when you use our website and application.",
      "For the purposes of UK data protection law (the UK GDPR and the Data Protection Act 2018), the data controller is Swishr Digital Ltd, a company registered in England and Wales under company number 17391541, with its registered office at Flat 6 Logan House, 1 Logan Place, London, United Kingdom, W8 6QN. If you have any questions, you can contact us in writing at that address.",
    ],
  },
  {
    h: "2. The information we collect",
    p: ["We collect and process the following categories of personal data:"],
    list: [
      "Account data \u2014 your name, email address, password (stored only as a secure hash), workspace name and role.",
      "Workspace content \u2014 the information you choose to add to your workspace, such as people, contacts, documents, renewals, tasks, calendar events and notes. This may include personal data about the principals and third parties you manage.",
      "AI input \u2014 voice recordings and text you submit to our AI features (for transcription, daily-brief generation and task suggestions).",
      "Usage and technical data \u2014 log data, device and browser information, IP address, and actions taken in the app, used for security and to improve the service.",
      "Cookies and similar technologies \u2014 see section 10.",
    ],
  },
  {
    h: "3. How and why we use your data",
    p: ["We use your personal data to:"],
    list: [
      "Provide, operate and secure the service, including authenticating you and keeping your workspace separate from others (multi-tenant isolation).",
      "Deliver AI features you request, such as transcribing voice notes and drafting your daily brief.",
      "Communicate with you about your account, security and service updates.",
      "Process payments and manage subscriptions (where applicable).",
      "Detect, prevent and investigate fraud, abuse and security incidents.",
      "Comply with our legal obligations.",
    ],
  },
  {
    h: "4. Our legal bases",
    p: ["We rely on the following legal bases under the UK GDPR:"],
    list: [
      "Performance of a contract \u2014 to provide the service you have signed up for.",
      "Legitimate interests \u2014 to secure and improve the service, prevent abuse, and run our business, where these interests are not overridden by your rights.",
      "Consent \u2014 for non-essential cookies and certain communications, which you can withdraw at any time.",
      "Legal obligation \u2014 where we must retain or disclose data to comply with the law.",
    ],
  },
  {
    h: "5. AI features",
    p: [
      "Some features use third-party AI providers (for example, to transcribe voice notes and generate your daily brief). The relevant content is sent to these providers solely to return a result to you. AI-generated suggestions are presented for your review and are not added to your records until you confirm them.",
      "We do not sell your data, and we do not permit our AI providers to use your content to train their general models where this option is available to us. You should avoid entering special-category data into AI features unless necessary.",
    ],
  },
  {
    h: "6. Sharing and sub-processors",
    p: [
      "We do not sell your personal data. We share it only with service providers (processors) who help us run the platform, under contracts that require them to protect it. These include cloud hosting and file storage, AI/transcription providers, email delivery, and payment processing.",
      "We may also disclose data where required by law, to enforce our terms, or to protect the rights, safety and security of our users and the public.",
    ],
  },
  {
    h: "7. International transfers",
    p: [
      "Some of our providers may process data outside the UK. Where this happens, we rely on appropriate safeguards such as UK \u2018adequacy\u2019 regulations or the International Data Transfer Agreement / Addendum to the EU Standard Contractual Clauses, so your data receives an equivalent level of protection.",
    ],
  },
  {
    h: "8. How long we keep your data",
    p: [
      "We keep personal data for as long as your account is active and as needed to provide the service. When you delete your workspace or account, we delete or anonymise the associated data within a reasonable period, except where we must retain certain records to meet legal, accounting or security obligations.",
    ],
  },
  {
    h: "9. Your rights",
    p: ["Under UK data protection law you have the right to:"],
    list: [
      "Access a copy of your personal data.",
      "Ask us to correct inaccurate or incomplete data.",
      "Ask us to delete your data in certain circumstances.",
      "Restrict or object to certain processing.",
      "Request portability of data you provided to us.",
      "Withdraw consent at any time, where we rely on consent.",
    ],
  },
  {
    h: "10. Cookies",
    p: [
      "We use essential cookies to keep you signed in and to keep the service secure. We may also use limited analytics to understand how the service is used. You can control non-essential cookies through your browser settings or any cookie controls we provide.",
    ],
  },
  {
    h: "11. Security",
    p: [
      "We take reasonable technical and organisational measures to protect your data, including encryption in transit, hashed passwords, access controls and tenant isolation. No system is perfectly secure, but we work to protect your information and to notify you and the ICO of any breach where legally required.",
    ],
  },
  {
    h: "12. Children",
    p: ["The service is intended for business use by adults and is not directed at children under 16. We do not knowingly collect data from children."],
  },
  {
    h: "13. Changes to this policy",
    p: ["We may update this policy from time to time. We will post the updated version here and, where changes are significant, notify you by email or in the app."],
  },
  {
    h: "14. Contact us and complaints",
    p: [
      "To exercise your rights or ask any question about this policy, contact us in writing at our registered office: Flat 6 Logan House, 1 Logan Place, London, United Kingdom, W8 6QN.",
      "If you are not satisfied with our response, you have the right to complain to the Information Commissioner\u2019s Office (ICO), the UK supervisory authority, at ico.org.uk or by calling 0303 123 1113.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testid="privacy-policy-page"
      title="Privacy Policy"
      updated="6 August 2026"
      intro="This Privacy Policy describes how Swishr Central collects, uses and protects your personal data, and the rights you have under UK data protection law."
      sections={sections}
    />
  );
}
